import { CanActivateFn, Router } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CookieService } from 'riterz-core';
import { environment } from '../../environments/environment';

/**
 * Requires an authenticated user (access token cookie present).
 *
 * **Layer 1** of the guard chain — every protected route starts here.
 * Redirects unauthenticated users to `/auth/login` with a `return_url` query param.
 *
 * SSR: returns `true` (cookies unavailable server-side).
 * OAuth redirect: allows through when `access_token` + `refresh_token` query params are present
 * so `AppComponent` can process the tokens first.
 *
 * Route combinations:
 * - `[ loggedInGuard ]` — status pages that any logged-in user can see
 *   (e.g. `/pending-approval`, `/registration-rejected`, `/account`)
 * - `[ loggedInGuard, approvedGuard ]` — standard app pages
 * - `[ loggedInGuard, approvedGuard, superAdminGuard ]` — admin pages
 */
export const loggedInGuard: CanActivateFn = (
  route,
  state,
  platformId = inject(PLATFORM_ID),
  router: Router = inject(Router),
  cookieService: CookieService = inject(CookieService),
) => {
  // Skip auth check on server — cookies aren't available during SSR
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const canActivate = !!cookieService.get(environment.riterz_access_token_name);

  if (canActivate) {
    return true;
  } else {
    // If url has refresh_token and access_token, we assume it's a login redirect and we can return true
    // Not ideal
    if (route.queryParams['refresh_token'] && route.queryParams['access_token']) {
      return true;
    } else {
      router.navigate(['/auth/login'], { queryParams: { return_url: state.url } });
      return false;
    }

  }
};
